// assets/custom-product-addons.js
// Aircare product add-ons (blocks/ac-product-addons.liquid), replacing the Globo Product Options app.
//
// Loaded on every page (snippets/custom-product-addons-head.liquid) because the cart drawer is.
// Two one-line hooks call into window.AircareAddons:
//   assets/product-form.js          buildAddToCartConfig: the unit and its add-ons in one request,
//                                   each add-on nested under the unit (Shopify nested cart lines)
//   assets/component-cart-items.js  cartQuantityRequest: an add-on follows its unit's quantity
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents } from '@shopify/events';

const ADDON_FIELD = /^ac_addon_/;
const PROPERTY_FIELD = /^properties\[(.+)\]$/;

/**
 * Builds the add to cart request when the form carries add-on inputs.
 * Returns null for any other form, so Horizon's own request goes ahead unchanged.
 * @param {HTMLFormElement} form
 * @param {FormData} formData
 * @returns {RequestInit | null}
 */
function buildAddToCartConfig(form, formData) {
  const addonKeys = [...new Set([...formData.keys()].filter((key) => ADDON_FIELD.test(key)))];
  if (addonKeys.length === 0) return null;

  const unitId = Number(formData.get('id'));
  const quantity = Number(formData.get('quantity')) || 1;
  const addonIds = [
    ...new Set(
      addonKeys
        .flatMap((key) => formData.getAll(key))
        .filter((value) => typeof value === 'string' && value !== '')
        .map(Number)
    ),
  ];

  /** @type {{id: number, quantity: number, properties: Record<string, string>, selling_plan?: number}} */
  const unit = { id: unitId, quantity, properties: {} };
  for (const [key, value] of formData.entries()) {
    const match = key.match(PROPERTY_FIELD);
    if (match && typeof value === 'string' && value !== '') unit.properties[match[1]] = value;
  }
  const sellingPlan = formData.get('selling_plan');
  if (sellingPlan) unit.selling_plan = Number(sellingPlan);

  const items = [unit];
  if (addonIds.length > 0) {
    // Different picks make different lines: the same unit with a 3m kit and with a 7m kit
    // must not merge. The signature also keeps identical add-ons under different units apart.
    const signature = [...addonIds].sort((a, b) => a - b).join(',');
    unit.properties._addons = signature;
    for (const id of addonIds) {
      items.push({ id, quantity, parent_id: unitId, properties: { _addon_for: `${unitId}:${signature}` } });
    }
  }

  /** @type {Record<string, unknown>} */
  const body = { items, sections_url: window.location.pathname };
  const sections = [
    ...new Set(
      formData
        .getAll('sections')
        .flatMap((value) => String(value).split(','))
        .filter(Boolean)
    ),
  ];
  if (sections.length > 0) body.sections = sections.join(',');

  return {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/javascript',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  };
}

/**
 * When a unit's quantity changes in the cart, changes its add-on lines to match, in one request.
 * Returns null when the line has no add-ons, or is being removed (Shopify removes the add-ons
 * with it), so Horizon's own request goes ahead unchanged.
 * @param {HTMLElement & {refs?: {cartItemRows?: HTMLElement[]}}} component
 * @param {{line: number, quantity: number}} config
 * @param {string} body Horizon's change request body
 * @returns {Promise<Response> | null}
 */
function cartQuantityRequest(component, config, body) {
  if (!config || config.quantity <= 0) return null;
  const rows = component.refs?.cartItemRows ?? [];
  const row = rows[config.line - 1];
  const key = row?.dataset.key;
  if (!key) return null;

  const children = rows.filter((candidate) => candidate.dataset.parentKey === key);
  if (children.length === 0) return null;

  /** @type {Record<string, number>} */
  const updates = { [key]: config.quantity };
  for (const child of children) {
    if (child.dataset.key) updates[child.dataset.key] = config.quantity;
  }
  const { sections, sections_url } = JSON.parse(body);

  return fetch(Theme.routes.cart_update_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ updates, sections, sections_url }),
  });
}

window.AircareAddons = { buildAddToCartConfig, cartQuantityRequest };

/**
 * Keeps each group's closed row showing its current pick, and the add-ons total and the
 * unit's total in step with the choices, the variant and the quantity on the product page.
 */
class AcProductAddons extends HTMLElement {
  connectedCallback() {
    this.basePrice = Number(this.dataset.basePrice) || 0;
    this.quantity = 1;
    this.addEventListener('change', this.update);
    // A required choice inside a closed row cannot take the browser's focus, so open it.
    this.addEventListener('invalid', this.onInvalid, true);

    this.section = this.closest('.shopify-section');
    this.section?.addEventListener(StandardEvents.productSelect, this.onProductSelect);
    this.section?.addEventListener('quantity-selector:update', this.onQuantityChange);
    this.update();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.update);
    this.removeEventListener('invalid', this.onInvalid, true);
    this.section?.removeEventListener(StandardEvents.productSelect, this.onProductSelect);
    this.section?.removeEventListener('quantity-selector:update', this.onQuantityChange);
  }

  /** @param {Event} event */
  onInvalid = (event) => {
    const details = /** @type {Element} */ (event.target).closest('details');
    if (details instanceof HTMLDetailsElement && !details.open) details.open = true;
  };

  /** @param {Event & {promise?: Promise<{variant?: {price?: {amount: string}}}>}} event */
  onProductSelect = (event) => {
    event.promise
      ?.then(({ variant }) => {
        if (!variant?.price?.amount) return;
        this.basePrice = Math.round(Number(variant.price.amount) * 100);
        this.update();
      })
      .catch(() => {});
  };

  /** @param {Event & {detail?: {quantity?: number, cartLine?: number}}} event */
  onQuantityChange = (event) => {
    if (event.detail?.cartLine) return;
    this.quantity = Number(event.detail?.quantity) || 1;
    this.update();
  };

  /** @param {number} amount in minor units */
  money(amount) {
    return formatMoney(amount, this.dataset.moneyFormat || '£{{amount}}', this.dataset.currency || 'GBP');
  }

  update = () => {
    let addons = 0;
    for (const group of this.querySelectorAll('[data-ac-group]')) {
      addons += this.updateGroup(group);
    }

    const summary = this.querySelector('[data-ac-ref="summary"]');
    if (!summary) return;
    summary.hidden = addons === 0;
    if (addons === 0) return;

    const addonsTotal = this.querySelector('[data-ac-ref="addonsTotal"]');
    const grandTotal = this.querySelector('[data-ac-ref="grandTotal"]');
    if (addonsTotal) addonsTotal.textContent = `+${this.money(addons * this.quantity)}`;
    if (grandTotal) grandTotal.textContent = this.money((this.basePrice + addons) * this.quantity);
  };

  /**
   * Writes a group's current pick into its closed row, and returns what it adds per unit.
   * @param {Element} group
   * @returns {number}
   */
  updateGroup(group) {
    /** @type {HTMLInputElement[]} */
    const choices = [...group.querySelectorAll('input[name^="ac_addon_"]')].filter((input) => input.value !== '');
    const picked = choices.filter((input) => input.checked);
    const price = picked.reduce((sum, input) => sum + (Number(input.dataset.price) || 0), 0);

    let label = this.dataset.noneLabel || 'None';
    if (picked.length > 1) {
      label = (this.dataset.countLabel || '[count] added').replace('[count]', String(picked.length));
    } else if (picked.length === 1) {
      const [input] = picked;
      label = choices.length === 1 && !input.hasAttribute('data-free') ? this.dataset.addedLabel || 'Added' : input.dataset.label || '';
    }

    let priceText = '';
    if (price > 0) priceText = `+${this.money(price)}`;
    else if (picked.some((input) => input.hasAttribute('data-free'))) priceText = this.dataset.freeLabel || 'Free';

    const valueLabel = group.querySelector('[data-ac-value-label]');
    const valuePrice = group.querySelector('[data-ac-value-price]');
    if (valueLabel) valueLabel.textContent = label;
    if (valuePrice) valuePrice.textContent = priceText ? `\u00a0\u00b7 ${priceText}` : '';
    group.querySelector('[data-ac-value]')?.toggleAttribute('data-selected', picked.length > 0);
    return price;
  }
}

if (!customElements.get('ac-product-addons')) {
  customElements.define('ac-product-addons', AcProductAddons);
}
